import styled from '@emotion/styled'
import UploadcareImage from '@uploadcare/nextjs-loader'
import Link from 'next/link'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import isUrl from 'validator/lib/isURL'
import Button from '../../components/Button'
import ConfirmModal from '../../components/ConfirmModal'
import Flash from '../../components/Flash'
import ImageUpload from '../../components/ImageUpload'
import RichTextInput from '../../components/RichTextInput'
import { useUser } from '../../hooks'
import { listCreate, listDelete, listUpdate } from '../../redux/actions'
import Input from '../Input2'
import ListFormItemsInput from './ListFormItemsInput'

const StyledButton = styled(Button)`
  @media screen and (max-width: 39em) {
    margin-bottom: 2rem;
    width: 100%;
  }

  @media screen and (min-width: 39em) {
    float: right;
  }
`

const ButtonContainer = styled.div`
  margin-bottom: 4rem;
  width: 100%;
`

const emptyList = {
  body: '',
  description: '',
  draft: false,
  hero_image: null,
  ListItems: [],
  name: '',
  slug: '',
  title: '',
  url: '',
}

const ListForm = ({ action, savedList, token }) => {
  const [user] = useUser()
  const dispatch = useDispatch()
  const loading = useSelector((state) => state.loading)
  const [list, setList] = useState(savedList)
  const [errors, setErrors] = useState({})
  const [modalOpen, setModalOpen] = useState(false)

  const valid = () => {
    const errors = {
      title: list.title.length < 1,
    }

    if (!user) {
      if (list.name.length < 1) errors.name = true

      if (list.url.length > 0) {
        if (!isUrl(list.url, { require_protocol: false })) {
          errors.url = true
        }
      }
    }

    if (Object.values(errors).includes(true)) {
      setErrors(errors)
      return false
    } else {
      return true
    }
  }

  const handleChange = ({ key, value }) => {
    setErrors({ ...errors, [key]: false })
    setList({ ...list, [key]: value })
  }

  const handleAddItem = (item) => {
    let dontAdd = false
    list.ListItems.some((track, i) => {
      if (item.url === track.url) dontAdd = true
    })
    if (!dontAdd) {
      handleChange({
        key: 'ListItems',
        value: list.ListItems.concat(item),
      })
    }
  }

  const handleClearItems = () => {
    handleChange({ key: 'ListItems', value: [] })
  }

  const handleDeleteItem = (index) => {
    const value = JSON.parse(JSON.stringify(list.ListItems))
    value.splice(index, 1)
    handleChange({ key: 'ListItems', value })
  }

  const handleRearrange = (obj) => {
    const item = list.ListItems[obj.source]
    const value = JSON.parse(JSON.stringify(list.ListItems))
    value.splice(obj.source, 1)
    value.splice(obj.destination, 0, item)
    value.forEach((item, i) => {
      item.order = i
    })
    handleChange({ key: 'ListItems', value })
  }

  const handleSubmit = (e, draft = false) => {
    e.preventDefault()

    if (valid()) {
      if (action === 'create') {
        dispatch(listCreate({ list, draft, user }))
      } else if (action === 'update') {
        dispatch(listUpdate({ list, draft, token, user }))
      }
    }
  }

  return (
    <>
      <ListFormItemsInput
        items={list.ListItems}
        onAddItem={handleAddItem}
        onClearItems={handleClearItems}
        onDeleteItem={handleDeleteItem}
        onRearrange={handleRearrange}
      />

      {!user && (
        <>
          <h2 className="f3 fw6 lh-copy mb3 mb4-ns mv0 tracked-light">
            Add your details
          </h2>

          <div className="f5 lh-copy mb4">
            Let listeners know who you are, where they can find you, and why you
            created this list for them.
          </div>

          <Input
            autoComplete="off"
            className="mb3"
            error={errors.name && 'is required'}
            label="Your name"
            maxLength="70"
            name="name"
            onChange={(e) =>
              handleChange({ key: 'name', value: e.target.value })
            }
            placeholder="eg. DJ Instagram Baby Wash"
            type="text"
            value={list.name}
          />

          <Input
            autoComplete="off"
            className="mb3"
            error={errors.url && 'is not a valid link'}
            label="Your personal URL (optional)"
            name="url"
            onChange={(e) =>
              handleChange({ key: 'url', value: e.target.value })
            }
            placeholder="eg. Your website, SoundCloud, Bandcamp"
            type="text"
            value={list.url}
          />
        </>
      )}

      {user && (
        <>
          <label className="f6 fw5 mb2">
            <b>Add an image to the top of your list</b> (optional)
            <br />
            rec. 1200x600px
            <br />
            max 5Mb
          </label>

          {list.hero_image && (
            <UploadcareImage
              alt="list image"
              className="br2 mb2"
              height={200}
              layout="responsive"
              src={list.hero_image + '.jpg'}
              width={400}
            />
          )}

          <ImageUpload
            className="mb4"
            crop="2:1"
            onChange={(file) =>
              handleChange({ key: 'hero_image', value: file.cdnUrl })
            }
            value={list.hero_image}
          />
        </>
      )}

      <Input
        autoComplete="off"
        className="mb3"
        error={errors.title && 'is required'}
        label="Your list's title"
        name="title"
        maxLength="100"
        onChange={(e) => handleChange({ key: 'title', value: e.target.value })}
        placeholder="e.g. My favorite Bandcamp releases"
        required
        type="text"
        value={list.title}
      />

      <Input
        className="mb3"
        label="A short description of your list (optional)"
        maxLength="150"
        name="description"
        onChange={(e) =>
          handleChange({ key: 'description', value: e.target.value })
        }
        placeholder="This text will be visible when you share your list on social media"
        type="text"
        value={list.description}
      />

      {user && (
        <RichTextInput
          className="mb3"
          label="Long text (optional)"
          onChange={(value) => handleChange({ key: 'body', value: value })}
          placeholder="You can write at length about your list here"
          value={list.body}
        />
      )}

      <Flash />

      <ButtonContainer className="mt3">
        <StyledButton
          disabled={list.ListItems.length === 0}
          loading={loading}
          onClick={handleSubmit}
        >
          Publish
        </StyledButton>

        {user && (
          <StyledButton
            className="mr3"
            disabled={list.ListItems.length === 0}
            loading={loading}
            onClick={(e) => handleSubmit(e, true)}
            secondary
          >
            Save as draft
          </StyledButton>
        )}

        <Link href="/">
          <a>
            <StyledButton className="mr3" loading={loading} secondary>
              Cancel
            </StyledButton>
          </a>
        </Link>

        {action === 'update' && (
          <StyledButton
            className="mr3"
            loading={loading}
            onClick={() => setModalOpen(true)}
            white
          >
            Delete list
          </StyledButton>
        )}
      </ButtonContainer>

      {modalOpen && (
        <ConfirmModal
          onClose={() => setModalOpen(false)}
          onConfirm={() => {
            dispatch(
              listDelete({
                slug: list.slug,
                token,
              })
            )
          }}
          text="Are you sure you want to delete this list?"
        />
      )}
    </>
  )
}

ListForm.defaultProps = {
  savedList: emptyList,
}

export default ListForm
